import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { logger } from '../utils/logger';
import './About.css';

interface ProjectStats {
  classes: number;
  methods: number;
  properties: number;
  versions: number;
}

interface VersionEntry {
  file: string;
}

interface VersionsIndex {
  stable: VersionEntry[];
  experimental: VersionEntry[];
}

export default function About() {
  const [stats, setStats] = useState<ProjectStats | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      try {
        const versionsRes = await fetch('/data/versions.json');
        if (!versionsRes.ok) return;
        const versions: VersionsIndex = await versionsRes.json();

        const versionCount =
          (versions.stable?.length || 0) + (versions.experimental?.length || 0);

        // Latest stable build is the first entry; read its class metadata
        const latest = versions.stable?.[0];
        if (!latest) return;

        const classesRes = await fetch(`/data/${latest.file}`);
        if (!classesRes.ok) return;
        const data = await classesRes.json();
        const meta = data.metadata || {};

        if (!cancelled) {
          setStats({
            classes: meta.total_classes || 0,
            methods: meta.total_methods || 0,
            properties: meta.total_properties || 0,
            versions: versionCount,
          });
        }
      } catch (error) {
        logger.error('Failed to load project statistics:', error);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatNumber = (value: number) => value.toLocaleString('en-US');

  return (
    <div className="about-page">
      <div className="about-container">
        <header className="about-header">
          <h1>About This Project</h1>
          <p className="about-subtitle">
            Transforming DayZ script documentation into an accessible, searchable, and comprehensive resource for the modding community
          </p>
        </header>
        
        <section className="about-section">
          <h2>Project Statistics</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{formatNumber(stats?.classes ?? 6182)}</div>
              <div className="stat-label">Classes Documented</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{formatNumber(stats?.methods ?? 28053)}</div>
              <div className="stat-label">Methods</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{formatNumber(stats?.properties ?? 11320)}</div>
              <div className="stat-label">Properties</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{formatNumber(stats?.versions ?? 29)}</div>
              <div className="stat-label">DayZ Versions Supported</div>
            </div>
          </div>
          <p className="stats-note">Latest stable build · updated automatically from the parsed DayZ scripts</p>
        </section>

        <section className="about-section">
          <h2>Credits & Acknowledgments</h2>

          <div className="credit-category">
            <h3>DayZ & Source Material</h3>
            <p>
              <strong><a href="https://www.bohemia.net/" target="_blank" rel="noopener noreferrer">Bohemia Interactive</a></strong> - For creating DayZ and making the EnScript source code available
              to the modding community. All DayZ scripts and intellectual property belong to Bohemia Interactive.
            </p>
            <p>
              Scripts sourced from the official <strong><a href="https://github.com/BohemiaInteractive/DayZ-Script-Diff" target="_blank" rel="noopener noreferrer">DayZ-Script-Diff</a></strong> repository.
            </p>
            <p className="credit-note">
              This project is for documentation purposes only and is not affiliated with or endorsed by Bohemia Interactive.
            </p>
          </div>

          <div className="credit-category">
            <h3>Parser Foundation</h3>
            <p>
              <strong><a href="https://github.com/RisageDev" target="_blank" rel="noopener noreferrer">RisageDev</a></strong> and <strong><a href="https://github.com/yuval" target="_blank" rel="noopener noreferrer">yuval</a></strong> - Early parser implementations that inspired our approach to
              EnScript parsing and inheritance detection. Their pioneering work on VS Code extensions demonstrated
              the feasibility of automated DayZ script analysis.
            </p>
          </div>

          <div className="credit-category">
            <h3>Open Source Libraries</h3>
            <ul className="credit-list">
              <li>
                <strong><a href="https://github.com/facebook/react" target="_blank" rel="noopener noreferrer">React & React DOM</a></strong> - MIT License, © Meta Platforms, Inc.
              </li>
              <li>
                <strong><a href="https://github.com/microsoft/TypeScript" target="_blank" rel="noopener noreferrer">TypeScript</a></strong> - Apache License 2.0, © Microsoft Corporation
              </li>
              <li>
                <strong><a href="https://github.com/vitejs/vite" target="_blank" rel="noopener noreferrer">Vite</a></strong> - MIT License, © Evan You & Vite contributors
              </li>
              <li>
                <strong><a href="https://github.com/d3/d3" target="_blank" rel="noopener noreferrer">D3.js</a></strong> - ISC License, © Mike Bostock
              </li>
              <li>
                <strong><a href="https://github.com/krisk/fuse" target="_blank" rel="noopener noreferrer">Fuse.js</a></strong> - Apache License 2.0, © Kirollos Risk
              </li>
              <li>
                <strong><a href="https://github.com/PrismJS/prism" target="_blank" rel="noopener noreferrer">Prism</a></strong> - MIT License, © Lea Verou
              </li>
              <li>
                <strong><a href="https://github.com/networkx/networkx" target="_blank" rel="noopener noreferrer">NetworkX</a></strong> - BSD License, © NetworkX Developers
              </li>
              <li>
                <strong><a href="https://github.com/pallets/jinja" target="_blank" rel="noopener noreferrer">Jinja2</a></strong> - BSD License, © Pallets
              </li>
            </ul>
          </div>
        </section>

        <section className="about-section">
          <h2>License</h2>
          <p>
            This project is licensed under the <strong>MIT License</strong>, allowing free use, modification,
            and distribution with attribution.
          </p>
          <p className="license-note">
            <strong>Important:</strong> DayZ scripts and game content are © Bohemia Interactive and are used
            for documentation purposes only. All trademarks and intellectual property belong to their respective owners.
          </p>
        </section>

        <section className="about-section about-footer">
          <h2>Get Involved</h2>
          <p>
            This project is open source and welcomes contributions from the community. Whether you're
            fixing bugs, adding features, improving documentation, or providing feedback - your input matters!
          </p>
          <div className="about-links">
            <a
              href="https://github.com/bauvdel/"
              target="_blank"
              rel="noopener noreferrer"
              className="about-link"
            >
              GitHub Repository
            </a>
            <a
              href="https://github.com/bauvdel"
              target="_blank"
              rel="noopener noreferrer"
              className="about-link"
            >
              Report Issues
            </a>
            <Link to="/docs-spa" className="about-link">
              Class Browser
            </Link>
          </div>
        </section>

        <footer className="about-copyright">
          <p>Made for the DayZ modding community</p>
          <p className="about-version">Version 0.1 • Last Updated: July 2026</p>
        </footer>
      </div>
    </div>
  );
}
